import { useState, useEffect, useCallback } from 'react'
import ProductPlanForm from './ProductPlanForm'
import { apiFetch } from '../utils/api'
import PropTypes from 'prop-types'

// The backend treats a missing or non-positive tester limit as unlimited.
const formatBetaLimit = (limit) => (limit == null || limit < 1 ? '∞' : limit)

// Beta: access mode and license usage; trial: trial days; otherwise: price info.
const formatPlanDetails = (plan) => {
  if (plan.billing_interval === 'beta') {
    const access = plan.beta_requires_registration === false ? 'open' : 'registration'
    return `${access}: ${plan.beta_active_licenses ?? '–'} / ${formatBetaLimit(plan.beta_max_licenses)}`
  }
  if (plan.billing_interval === 'trial') {
    return plan.trial_days
  }
  return plan.price_info
}

const ProductPlanAdministrationContent = ({ softwareProduct, onBack }) => {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [replaceKeyPlanId, setReplaceKeyPlanId] = useState(null)
  const [editingPlan, setEditingPlan] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [publicKey, setPublicKey] = useState(null)
  const [selectedPlanIdForKey, setSelectedPlanIdForKey] = useState(null)

  // Load plans from API
  const loadPlans = useCallback(async () => {
    try {
      const response = await apiFetch(`/api/admin/software-products/${softwareProduct.id}/plans`)
      if (response.ok) {
        const data = await response.json()
        setPlans(data.plans || [])
      } else {
        setError('Failed to load plans')
      }
    } catch (err) {
      setError('Network error while loading plans')
      console.error('Error loading plans:', err)
    } finally {
      setLoading(false)
    }
  }, [softwareProduct.id])

  useEffect(() => {
    loadPlans()
  }, [loadPlans])

  const handleAdd = () => {
    setEditingPlan(null)
    setIsEditing(false)
    setShowForm(true)
  }

  const handleEdit = (plan) => {
    setEditingPlan(plan)
    setIsEditing(true)
    setShowForm(true)
  }

  const handleDelete = async (plan) => {
    if (!globalThis.confirm('Möchten Sie diesen Plan wirklich löschen?')) return
    try {
      const response = await apiFetch(`/api/admin/software-products/${softwareProduct.id}/plans/${plan.id}`, { method: 'DELETE' })
      if (response.ok) {
        await loadPlans()
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to delete plan')
      }
    } catch (err) {
      setError('Network error. Please check backend server.')
      console.error('Delete plan error:', err)
    }
  }

  const handleSave = async (planData) => {
    try {
      const endpoint = isEditing
        ? `/api/admin/software-products/${softwareProduct.id}/plans/${editingPlan.id}`
        : `/api/admin/software-products/${softwareProduct.id}/plans`
      const response = await apiFetch(endpoint, { method: isEditing ? 'PUT' : 'POST', body: JSON.stringify(planData) })
      if (response.ok) {
        await loadPlans()
        setShowForm(false)
        setEditingPlan(null)
        setIsEditing(false)
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to save plan')
      }
    } catch (err) {
      setError('Network error. Please check backend server.')
      console.error('Save plan error:', err)
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingPlan(null)
    setIsEditing(false)
  }

  const handleGenerateKeypair = async (planId, force = false) => {
    try {
      const suffix = force ? '?force=true' : ''
      const response = await apiFetch(`/api/admin/plans/${planId}/generate-keypair${suffix}`, { method: 'POST' })
      if (response.ok) {
        setReplaceKeyPlanId(null)
        await loadPlans()
        return
      }
      const data = await response.json()
      if (response.status === 409 && data.code === 'key_exists' && !force) {
        setReplaceKeyPlanId(planId)
        return
      }
      setReplaceKeyPlanId(null)
      setError(data.error || 'Failed to generate keypair')
    } catch (err) {
      setError('Network error while generating keypair')
      console.error('Generate keypair error:', err)
    }
  }

  const handleShowPublicKey = async (planId) => {
    try {
      const response = await apiFetch(`/api/admin/plans/${planId}/public-key`)
      if (response.ok) {
        const data = await response.json()
        setPublicKey(data.public_key)
        setSelectedPlanIdForKey(planId)
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to load public key')
      }
    } catch (err) {
      setError('Network error while loading public key')
      console.error('Load public key error:', err)
    }
  }

  const handleDownloadSwift = async () => {
    if (!selectedPlanIdForKey) return

    try {
      const response = await apiFetch(`/api/admin/plans/${selectedPlanIdForKey}/secrets-swift`)
      if (response.ok) {
        const blob = await response.blob()
        const url = globalThis.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'Secrets.generated.swift'
        document.body.appendChild(a)
        a.click()
        globalThis.URL.revokeObjectURL(url)
        a.remove()
      } else {
        setError('Failed to download Swift file')
      }
    } catch (err) {
      setError('Network error while downloading Swift file')
      console.error('Download Swift file error:', err)
    }
  }

  if (loading) {
    return (
      <div className="content-section">
        <button className="btn-primary" onClick={onBack}>← Back</button>
        <h2>Plans for {softwareProduct.name}</h2>
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          Loading...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="content-section">
        <button className="btn-primary" onClick={onBack}>← Back</button>
        <h2>Plans for {softwareProduct.name}</h2>
        <div className="error-message">{error}</div>
      </div>
    )
  }

  return (
    <div className="content-section">
      <button className="btn-primary" onClick={onBack}>← Back</button>
      <h2>Plans for {softwareProduct.name}</h2>
      <div className="user-controls">
        <button className="btn-primary" onClick={handleAdd}>
          Add Plan
        </button>
      </div>
      {error && (
        <div className="error-message" style={{ position: 'relative', marginBottom: 16 }}>
          <span>{error}</span>
          <button
            onClick={() => setError('')}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              background: 'none',
              border: 'none',
              color: '#fca5a5',
              fontSize: 18,
              fontWeight: 'bold',
              cursor: 'pointer',
              lineHeight: 1
            }}
            aria-label="Close error message"
          >
            ×
          </button>
        </div>
      )}
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Paddle Plan ID</th>
              <th>Interval</th>
              <th>Price Info / Trial Days</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', color: '#9ca3af' }}>No plans found</td></tr>
            ) : (
              plans.map(planObj => {
                // Backend liefert: { plan: {...}, keypair_exists: true/false }
                const plan = planObj.plan || planObj
                const keypairExists = planObj.keypair_exists
                return (
                  <tr key={plan.id}>
                    <td>{plan.id}</td>
                    <td>{plan.name}</td>
                    <td>{plan.paddle_plan_id}</td>
                    <td>{plan.billing_interval}</td>
                    <td>
                      {formatPlanDetails(plan)}
                    </td>
                    <td>
                      <button className="btn-edit" onClick={() => handleEdit(plan)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(plan)}>Delete</button>
                      {keypairExists ? (
                        <button className="btn-edit" style={{ marginLeft: 8, background: 'rgba(16, 185, 129, 0.2)', color: '#34d399' }} onClick={() => handleShowPublicKey(plan.id)}>
                          Show Key
                        </button>
                      ) : (
                        <button className="btn-generate-keypair" style={{ marginLeft: 8 }} onClick={() => handleGenerateKeypair(plan.id)}>
                          Generate Keypair
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {replaceKeyPlanId && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Schlüsselpaar ersetzen</h3>
              <button className="close-btn" onClick={() => setReplaceKeyPlanId(null)}>×</button>
            </div>
            <p style={{ color: '#e5e7eb', marginBottom: '16px' }}>
              Für diesen Plan existiert bereits ein Schlüsselpaar. Ausgegebene Lizenzen werden ungültig, wenn es ersetzt wird.
            </p>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setReplaceKeyPlanId(null)}>Abbrechen</button>
              <button type="button" className="btn-delete" onClick={() => handleGenerateKeypair(replaceKeyPlanId, true)}>Schlüssel ersetzen</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <ProductPlanForm
          plan={editingPlan}
          onSave={handleSave}
          onCancel={handleCancel}
          isEditing={isEditing}
        />
      )}

      {publicKey && (
        <div className="user-form-overlay">
          <div className="user-form-container" style={{ width: '700px' }}>
            <div className="user-form-header">
              <h3>Public Key</h3>
              <button className="close-btn" onClick={() => { setPublicKey(null); setSelectedPlanIdForKey(null); }}>×</button>
            </div>
            <div className="user-form">
              <div className="form-group">
                <label htmlFor="public-key-textarea">Copy this key into your application:</label>
                <textarea
                  id="public-key-textarea"
                  readOnly
                  value={publicKey}
                  style={{ width: '100%', height: '300px', fontFamily: 'monospace', fontSize: '0.85rem' }}
                  onClick={(e) => e.target.select()}
                />
              </div>
              <div className="form-actions">
                <button className="btn-primary" onClick={() => { navigator.clipboard.writeText(publicKey).then(() => alert('Copied to clipboard!'), () => alert('Failed to copy to clipboard')); }}>
                  Copy to Clipboard
                </button>
                <button className="btn-primary" style={{ background: 'linear-gradient(135deg, #F05138 0%, #F56D56 100%)' }} onClick={handleDownloadSwift}>
                  Swift
                </button>
                <button className="btn-cancel" onClick={() => { setPublicKey(null); setSelectedPlanIdForKey(null); }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductPlanAdministrationContent

// Validate props for ProductPlanAdministrationContent
ProductPlanAdministrationContent.propTypes = {
  softwareProduct: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    paddle_product_id: PropTypes.string,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
} 